import React, { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import styled from 'styled-components';
import {
  Button,
  Flexbox,
  TextInput,
  SingleSelect
} from '@ibinder/common-frontend';
import ListDocument from '../ListDocument';
import { assignmentsQuery, GET_USER_ASSIGNMENTS } from './getUserAssignments';
import {
  UserAssignment,
  UserAssignmentsByOrganizationResponse
} from './userResourceAccess.types';

export type AssignmentOrganization = {
  id: string;
  name: string;
};

type Props = {
  userId: string;
  userName: string;
  organizations: AssignmentOrganization[];
  onBack: () => void;
};

type SortBy = {
  id?: string;
  desc: boolean | null;
};

type AssignmentRow = {
  id: string;
  organizationName: string;
  assignmentType: string;
  resourceName: string;
  status: string;
  statusCode: string;
  dueDate: string;
  responsibility: string;
  binderName: string;
  binderId: string;
};

const ALL_OPTION = 'All';

const Container = styled.article`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.25rem;
  margin: 0 0 1rem 0;
`;

const Filters = styled(Flexbox)`
  align-items: flex-end;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const SearchInput = styled(TextInput)`
  max-width: 18rem;
`;

const FilterSelect = styled(SingleSelect)`
  min-width: 12rem;
`;

const ClearFiltersButton = styled(Button)`
  font-size: 0.875rem;
  height: 2rem;
  min-width: auto;
  width: auto;
`;

const ResultCount = styled.p`
  color: ${({ theme }) => theme.colors.discrete};
  margin: 0 0 0.5rem 0;
`;

type StatusTone = 'neutral' | 'warning' | 'error' | 'success';

const StatusPill = styled.span<{ $tone: StatusTone }>`
  background: ${({ theme, $tone }) =>
    $tone === 'error'
      ? theme.colors.error
      : $tone === 'success'
      ? theme.colors.success
      : $tone === 'warning'
      ? '#f59e0b'
      : theme.colors.primary10};
  border-radius: 1rem;
  color: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.primary : '#fff'};
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.125rem 0.625rem;
  white-space: nowrap;
`;

const BackButtonContainer = styled(Flexbox)`
  justify-content: flex-start;
  margin-top: auto;
  padding-top: 1rem;
`;

const BackButton = styled(Button)`
  font-size: 0.875rem;
  height: 2rem;
  min-width: auto;
  width: auto;
`;

const StatusMessage = styled.p`
  margin: 1rem 0;
`;

const statusTone = (statusCode: string): StatusTone => {
  const code = (statusCode || '').toLowerCase();
  if (code.indexOf('overdue') >= 0) {
    return 'error';
  }
  if (code.indexOf('due') >= 0 || code.indexOf('progress') >= 0) {
    return 'warning';
  }
  if (code.indexOf('complete') >= 0 || code.indexOf('closed') >= 0) {
    return 'success';
  }
  return 'neutral';
};

const capitalize = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : '';

const formatDueDate = (dueDate: string | null) => {
  if (!dueDate) {
    return '';
  }
  const parsed = new Date(dueDate);
  return isNaN(parsed.getTime()) ? dueDate : parsed.toLocaleDateString();
};

const toAssignmentRow = (
  assignment: UserAssignment,
  organization: AssignmentOrganization
): AssignmentRow => ({
  id: `${organization.id}:${assignment.resourceId}`,
  organizationName: organization.name || organization.id,
  assignmentType: capitalize(assignment.domain),
  resourceName: assignment.resourceName || '',
  status: assignment.status?.label || assignment.status?.code || '',
  statusCode: assignment.status?.code || '',
  dueDate: formatDueDate(assignment.dueDate),
  responsibility:
    assignment.responsibility?.label || assignment.responsibility?.code || '',
  binderName: assignment.binder?.name || '',
  binderId: assignment.binder?.id || ''
});

const assignmentColumns = [
  {
    Header: 'Organization',
    accessor: 'organizationName',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Assignment type',
    accessor: 'assignmentType',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Resource name',
    accessor: 'resourceName',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Binder',
    accessor: 'binderName',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Binder Id',
    accessor: 'binderId',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Status',
    accessor: 'status',
    sortType: 'alphanumeric',
    Cell: ({ row }) =>
      row.original.status ? (
        <StatusPill $tone={statusTone(row.original.statusCode)}>
          {row.original.status}
        </StatusPill>
      ) : (
        ''
      )
  },
  {
    Header: 'Due date',
    accessor: 'dueDate',
    sortType: 'alphanumeric'
  },
  {
    Header: 'Role / Responsibility',
    accessor: 'responsibility',
    sortType: 'alphanumeric'
  }
];

const uniqueValues = (values: string[]) =>
  values
    .filter((value, index) => value && values.indexOf(value) === index)
    .sort((a, b) => a.localeCompare(b));

const AllUserAssignments = ({
  userId,
  userName,
  organizations,
  onBack
}: Props) => {
  const client = useApolloClient();
  const [sortBy, setSortBy] = useState<SortBy>({
    id: 'dueDate',
    desc: false
  });
  const [search, setSearch] = useState('');
  const [typeIndex, setTypeIndex] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const [rows, setRows] = useState<AssignmentRow[]>([]);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadAllAssignments = async () => {
      setLoading(true);
      setSearch('');
      setTypeIndex(0);
      setStatusIndex(0);
      setRows([]);
      setFailed(false);

      if (organizations.length === 0) {
        setLoading(false);
        return;
      }

      try {
        const result = await client.query({
          query: GET_USER_ASSIGNMENTS,
          variables: {
            userId,
            body: organizations.map((organization) => organization.id),
            query: assignmentsQuery()
          },
          fetchPolicy: 'no-cache'
        });
        if (cancelled) {
          return;
        }
        if (result.errors?.length) {
          setFailed(true);
          setLoading(false);
          return;
        }
        const response = result.data?.getUserAssignments as
          | UserAssignmentsByOrganizationResponse
          | undefined;
        const organizationsById = new Map(
          organizations.map((organization) => [
            organization.id.toLowerCase(),
            organization
          ])
        );
        setRows(
          (response?.byOrganization || []).reduce((collected, entry) => {
            const organization = organizationsById.get(
              (entry.organizationId || '').toLowerCase()
            ) || {
              id: entry.organizationId,
              name: entry.organizationId
            };
            return collected.concat(
              (entry.assignments || []).map((assignment) =>
                toAssignmentRow(assignment, organization)
              )
            );
          }, [] as AssignmentRow[])
        );
      } catch (loadError) {
        if (!cancelled) {
          setFailed(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadAllAssignments();

    return () => {
      cancelled = true;
    };
  }, [client, userId, organizations]);

  const typeOptions = useMemo(
    () => [ALL_OPTION, ...uniqueValues(rows.map((row) => row.assignmentType))],
    [rows]
  );
  const statusOptions = useMemo(
    () => [ALL_OPTION, ...uniqueValues(rows.map((row) => row.status))],
    [rows]
  );

  const selectedType = typeOptions[typeIndex] || ALL_OPTION;
  const selectedStatus = statusOptions[statusIndex] || ALL_OPTION;
  const searchTerm = search.trim().toLowerCase();

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const matchesSearch =
          !searchTerm ||
          row.organizationName.toLowerCase().indexOf(searchTerm) >= 0 ||
          row.resourceName.toLowerCase().indexOf(searchTerm) >= 0 ||
          row.binderName.toLowerCase().indexOf(searchTerm) >= 0 ||
          row.binderId.toLowerCase().indexOf(searchTerm) >= 0;
        const matchesType =
          selectedType === ALL_OPTION || row.assignmentType === selectedType;
        const matchesStatus =
          selectedStatus === ALL_OPTION || row.status === selectedStatus;
        return matchesSearch && matchesType && matchesStatus;
      }),
    [rows, searchTerm, selectedType, selectedStatus]
  );

  const hasFilters =
    !!searchTerm ||
    selectedType !== ALL_OPTION ||
    selectedStatus !== ALL_OPTION;

  const clearFilters = () => {
    setSearch('');
    setTypeIndex(0);
    setStatusIndex(0);
  };

  return (
    <Container>
      <Title>{`All assignments for ${userName}`}</Title>

      {failed ? (
        <StatusMessage>Unable to load assignments.</StatusMessage>
      ) : !loading && rows.length === 0 ? (
        <StatusMessage>{`No assignments for ${userName}.`}</StatusMessage>
      ) : (
        <>
          <Filters>
            <SearchInput
              name="allAssignmentSearch"
              label="Search assignments"
              placeholder="Search assignments..."
              value={search}
              changeValue={(event) => setSearch(event?.target?.value || '')}
            />
            <FilterSelect
              label="Assignment type"
              dropdownText="All"
              activeIndex={typeIndex}
              dropdownData={typeOptions.map((option) => ({
                Key: option,
                Value: option
              }))}
              onClickSelectItem={setTypeIndex}
              withSortButton={false}
            />
            <FilterSelect
              label="Status"
              dropdownText="All"
              activeIndex={statusIndex}
              dropdownData={statusOptions.map((option) => ({
                Key: option,
                Value: option
              }))}
              onClickSelectItem={setStatusIndex}
              withSortButton={false}
            />
            <ClearFiltersButton
              btnStyle="secondary"
              label="Clear filters"
              disabled={!hasFilters}
              onClick={clearFilters}
            />
          </Filters>
          <ResultCount>
            {loading
              ? 'Loading assignments...'
              : `Showing ${filteredRows.length} of ${rows.length} assignments`}
          </ResultCount>
          <ListDocument
            data={filteredRows}
            columns={assignmentColumns}
            onSort={setSortBy}
            sortBy={sortBy}
            selectedIds={[]}
            setSelectedIds={() => undefined}
            hasSelectBox={false}
            fetchMore={() => undefined}
            hasFetchMore={() => false}
            loading={loading}
            type="AllUserAssignments"
            truncateFirstColumn={false}
            fullContentColumnIds={[
              'organizationName',
              'resourceName',
              'binderName'
            ]}
          />
        </>
      )}

      <BackButtonContainer>
        <BackButton btnStyle="secondary" label="Back" onClick={onBack} />
      </BackButtonContainer>
    </Container>
  );
};

export default AllUserAssignments;
